import type { BadgeVariant } from '../components/symphony/ui/Badge';
import { awaitsDecision, type AgentTaskBucket, type BucketableTask, bucketContaining, bucketOfTask, isInMotion, isNotStarted } from './agent-task-buckets';

// The components-sheet status-badge vocabulary. Every task-status badge shows its coarse BUCKET
// (Action needed / In progress / Completed / Failed) — label AND colour — derived from the single
// `bucketContaining` rule (mirrors native `AgentTaskBucket.badgeLabelKey` + `bucketChip`). So a PAUSED
// task reads "Action needed". The exceptions are the two terminal-but-NOT-accomplished statuses, MOOT
// ("Retired") and CANCELLED ("Cancelled"), which stay in the `done` bucket but wear the quiet neutral
// chip on EVERY surface; plus the task-DETAIL page (`taskStatusBadge(status, { detail: true })`), which
// shows IN_REVIEW as "In review".
//
// `TASK_STATUS_BADGE` below stays as the per-status variant SOURCE the tab pills + stat cards read for a
// specific bucket-representative status's colour; it is no longer a label source.
//
// It hands out a BADGE STYLE, never a colour. Each style owns its `--ds-badge-*`
// fill/ink/border triple in the token layer (see styles/design-tokens.css), so the
// render site is `<Badge variant={...}>` and nothing here knows a hex — the same shape
// the progress bar's `progress` level has.
//
// `labelKey` is an i18n key (not literal text) — the label is localized at the render
// site via `t(badge.labelKey)`, mirroring the native TaskScreen.
/** The nine styles that own a `--ds-badge-*` fill/ink/border triple. The three date
 *  buckets, the WhatsApp group's mint `active` pill and the routine offer's blue `recommended`
 *  pill are deliberately outside it — they paint from other tokens, and nothing echoes them. */
export type TokenBackedBadgeVariant = Exclude<BadgeVariant, 'this-week' | 'next-week' | 'in-three-weeks' | 'active' | 'recommended' | 'coming-soon'>;

export interface TaskStatusBadge {
  labelKey: string;
  variant: TokenBackedBadgeVariant;
}

export const TASK_STATUS_BADGE: Record<string, TaskStatusBadge> = {
  ACTION_NEEDED: { labelKey: 'tasks.status.actionNeeded', variant: 'waiting-on-you' },
  PENDING: { labelKey: 'tasks.status.actionNeeded', variant: 'waiting-on-you' },
  // The STORED status, which is not a verdict: a row left IN_PROGRESS with nobody on it is the
  // seventeen-day task the island already learned about. `Working` is earned by `isInMotion`, never
  // by this.
  IN_PROGRESS: { labelKey: 'tasks.status.activeTask', variant: 'in-progress' },
  // The v2 AgentTask reviewer stage — output being checked, still in motion.
  IN_REVIEW: { labelKey: 'tasks.status.inReview', variant: 'in-progress' },
  PAUSED: { labelKey: 'tasks.status.paused', variant: 'paused' },
  WAITING: { labelKey: 'tasks.status.waiting', variant: 'waiting-on-a-reply' },
  COMPLETED: { labelKey: 'tasks.status.done', variant: 'done' },
  // The v2 AgentTask terminal FAILURE — its own alert-red badge (matches native's
  // `failed` status); without this entry a failed task falls through to the
  // in-progress tint and reads as "In progress".
  FAILED: { labelKey: 'tasks.status.failed', variant: 'failed' },
  // The v2 AgentTask terminal cancellation — the quiet neutral badge. Like MOOT it files under `done`
  // (it is terminal) but must NEVER wear the green Done chip: the user called this task off, and a
  // called-off task was not accomplished.
  CANCELLED: { labelKey: 'tasks.status.cancelled', variant: 'waiting-on-a-reply' },
  // Not started: laid out and never begun. DERIVED, never a status — it is keyed here (rather than off a
  // status string) purely so the one neutral treatment lives beside Retired's, its sibling in every way:
  // both stay in the bucket their status puts them in, and both must not wear that bucket's chip.
  NOT_STARTED: { labelKey: 'tasks.status.notStarted', variant: 'waiting-on-a-reply' },
  // Retired: the world moved past this task before the agent got to it. It files under `done` (it is
  // terminal), but it must NEVER wear the green Done chip — a retired task was not accomplished. The DS's
  // one neutral grey Style (`waiting-on-a-reply`, fill `--ds-badge-fill-waiting-on-a-reply` = the light-gray
  // surface the design names) is that treatment; no new Style is invented for it.
  MOOT: { labelKey: 'tasks.status.retired', variant: 'waiting-on-a-reply' },
};

/** Bucket → badge {label, variant}. Every task-status badge shows this — the 4 buckets only. */
/** The ask an inbound outreach makes, in place of its bucket's label. Same Style — it IS the waiting
 *  bucket and must keep its colour and its place in the counts; only the word changes. */
const OUTREACH_REPLY_BADGE: TaskStatusBadge = { labelKey: 'chat.reply.action', variant: 'waiting-on-you' };

/**
 * The badge a BUCKET wears, with no task in hand.
 *
 * Exported because a bucket is a thing the UI counts and filters by, not only a thing a task lands in:
 * the Overview home's category cards, its state picker and its rows all need "what colour is
 * `waiting`?" and must agree. They used to answer it from three private copies of this table.
 */
export const BUCKET_BADGE: Record<AgentTaskBucket, TaskStatusBadge> = {
  // The STATUS BADGE reads "Waiting on you" (matching the DS Badge frame 434-273 and the agents
  // island), while the Tasks FILTER TAB keeps "Action Needed" — the tab owns its own literal
  // `tasks.status.actionNeeded` in `TaskTabs.tsx`, so the two no longer share one string.
  waiting: { labelKey: 'tasks.status.waitingOnYou', variant: 'waiting-on-you' },
  inProgress: { labelKey: 'tasks.status.activeTask', variant: 'in-progress' },
  done: { labelKey: 'tasks.status.done', variant: 'done' },
  failed: { labelKey: 'tasks.status.failed', variant: 'failed' },
};

/**
 * The task-status badge: its BUCKET's label + colour (the 4 buckets). `opts.detail` — the task-DETAIL
 * page ONLY — is the single exception: IN_REVIEW shows "In review" (still the in-progress blue), so a
 * reviewer stage is legible on the detail screen. Cards / goal-plan chips / tabs pass no `detail` and
 * get the plain bucket badge (IN_REVIEW → "In progress"). Mirrors native `taskDetailStatusLabel`.
 */
/**
 * A loop is executing on this row RIGHT NOW — the same verdict the header pill reads (`isInMotion`),
 * said in the same word. Distinct from the bucket's "Active Task", which means the work is released and
 * WILL be carried out but nothing is running yet.
 *
 * Splitting the two is what makes answering a question visibly do something. The badge used to read
 * "In progress" for both, so pressing Send it changed nothing on screen until an agent happened to pick
 * the row up — seconds later, and with no state in between to show. Reported as "it takes many many
 * seconds until the other indications reflect it".
 */
const WORKING_BADGE: TaskStatusBadge = { labelKey: 'tasks.status.working', variant: 'in-progress' };

export function taskStatusBadge(
  status: string,
  opts?: { detail?: boolean; notStarted?: boolean; awaitingDecision?: boolean; outreach?: boolean; inMotion?: boolean },
): TaskStatusBadge {
  // A task holding an OPEN question at the reader is Action needed, whatever its status says — the
  // backend never writes a status on the block (see `awaitsDecision`), so a blocked goal-plan task is
  // `PENDING` and this function, which only ever sees a status string, would badge it "In progress"
  // while `bucketOfTask` files the very same row under Action needed. A row bucketed one way and badged
  // another is the drift the single bucket rule exists to prevent, so the caller derives the flag from
  // the whole row and passes it, exactly as it does for `notStarted`.
  //
  // FIRST, ahead of `notStarted`: a task can be both undated-and-never-begun AND blocked on a question,
  // and of the two facts only one is the reader's to act on.
  if (opts?.awaitingDecision) return BUCKET_BADGE.waiting;
  // A task laid out and never begun buckets as `inProgress` — correctly, it IS the agent's turn — and the
  // bucket badge would therefore have it announce "In progress" with no agent anywhere near it. Two of
  // these sat on a real Tasks screen doing exactly that. Same shape as the MOOT exception below: the row
  // keeps its bucket and its count, and only stops claiming activity. The caller derives the flag
  // (`isNotStarted`), because the rule needs the whole row and this takes a status string.
  if (opts?.notStarted) return TASK_STATUS_BADGE.NOT_STARTED;
  // An inbound OUTREACH waiting on the reader is not a task the agent parked — it is a person who wrote
  // to them — so the badge names the ask instead of restating the bucket. "Action Needed" on a row
  // titled "Message from Yonatan" says a thing needs doing and not WHAT, which is how six rows came to
  // wear one badge for four different asks (reported on the island's own waiting card). Only while it is
  // still the reader's turn: an outreach they have answered files under `done` and must not keep asking
  // for a reply. The label is the chat's own `Reply`, not a new string — one word for one action.
  if (opts?.outreach && bucketContaining(status) === 'waiting') return OUTREACH_REPLY_BADGE;
  if (opts?.detail && status === 'IN_REVIEW') {
    return { labelKey: 'tasks.status.inReview', variant: 'in-progress' };
  }
  // MOOT buckets as `done` and must keep doing so — but the bucket badge is the GREEN "Done" chip, and a
  // retired task must never read as accomplished. This is the second exception to "the badge is its
  // bucket", and it is the whole point of the status.
  if (status === 'MOOT') return TASK_STATUS_BADGE.MOOT;
  // CANCELLED is the same argument as MOOT, and was the same bug: a task the USER deliberately called off
  // rendered the green "Done" chip, so the screen you land on after confirming "this cannot be undone"
  // announced the task as accomplished. It stays in the `done` bucket (it is terminal, and the tab counts
  // depend on it) and only stops claiming success. UNGATED by `detail`, exactly like MOOT — a cancelled
  // task must not read as accomplished on a card or a tab either. `TASK_STATUS_BADGE.CANCELLED` already
  // held the right label and the neutral style; nothing returned it until now.
  if (status === 'CANCELLED') return TASK_STATUS_BADGE.CANCELLED;
  // GATED on the inProgress bucket, and that gate is the whole correctness of it: a PAUSED row with a
  // live run, or one holding an open question, is still the READER's turn and must keep saying so. Only
  // a row already filed as the agent's turn can upgrade from "Active Task" to "Working".
  if (opts?.inMotion && bucketContaining(status) === 'inProgress') return WORKING_BADGE;
  return BUCKET_BADGE[bucketContaining(status)];
}

/**
 * The STATUS a collapsed origin fold's header states — "3 waiting on you", "2 active tasks", "4 done".
 *
 * Derived from the fold's TASKS (decision-aware through `bucketOfTask`, exactly like every card inside the
 * fold), never from one task's status. Android's header read the lead's status-only label, so a goal plan
 * held on one question (`PENDING` on the wire) announced "3 Active Task" over three cards reading "Waiting
 * on you" (reported from the Action-needed tab); web and iOS each spoke for one task in their own way.
 *
 * **The most urgent state in the fold wins**: waiting on you > active (or, when no task in it has begun,
 * not started) > failed > done > cancelled > retired. `groupTaskOrigins` folds one bucket only, so the fold
 * is unanimous in practice; the ladder keeps the header honest if that ever loosens.
 *
 * Each kind owns a countable key pair — "{count} {status}" glued a count onto a singular badge word
 * ("3 Active Task") in every language. Shared 1:1 with native `TaskGroupStatus` (iOS + Android).
 */
export type TaskGroupStatus = 'waiting' | 'notStarted' | 'active' | 'failed' | 'done' | 'cancelled' | 'retired';

const TASK_GROUP_STATUS_COPY: Record<TaskGroupStatus, { one: string; other: string; variant: TokenBackedBadgeVariant }> = {
  waiting: { one: 'tasks.groupStatus.waiting.one', other: 'tasks.groupStatus.waiting.other', variant: BUCKET_BADGE.waiting.variant },
  notStarted: { one: 'tasks.groupStatus.notStarted.one', other: 'tasks.groupStatus.notStarted.other', variant: TASK_STATUS_BADGE.NOT_STARTED.variant },
  active: { one: 'tasks.groupStatus.active.one', other: 'tasks.groupStatus.active.other', variant: BUCKET_BADGE.inProgress.variant },
  failed: { one: 'tasks.groupStatus.failed.one', other: 'tasks.groupStatus.failed.other', variant: BUCKET_BADGE.failed.variant },
  done: { one: 'tasks.groupStatus.done.one', other: 'tasks.groupStatus.done.other', variant: BUCKET_BADGE.done.variant },
  cancelled: { one: 'tasks.groupStatus.cancelled.one', other: 'tasks.groupStatus.cancelled.other', variant: TASK_STATUS_BADGE.CANCELLED.variant },
  retired: { one: 'tasks.groupStatus.retired.one', other: 'tasks.groupStatus.retired.other', variant: TASK_STATUS_BADGE.MOOT.variant },
};

/** The fold's status (see {@link TaskGroupStatus}). Null only for an empty list. Pure. */
export function taskGroupStatus(tasks: BucketableTask[], now: number = Date.now()): TaskGroupStatus | null {
  if (tasks.length === 0) return null;
  const buckets = tasks.map((task) => bucketOfTask(task));
  if (buckets.includes('waiting')) return 'waiting';
  const active = tasks.filter((_, i) => buckets[i] === 'inProgress');
  if (active.length > 0) return active.every((task) => isNotStarted(task, now)) ? 'notStarted' : 'active';
  if (buckets.includes('failed')) return 'failed';
  if (tasks.some((task) => task.status !== 'CANCELLED' && task.status !== 'MOOT')) return 'done';
  if (tasks.some((task) => task.status === 'CANCELLED')) return 'cancelled';
  return 'retired';
}

/**
 * Whether the fold header adds its "{n} working now" chip. Not beside the active chip: "2 Working" next to
 * "1 working now" said the same thing twice, and QA asked which one was right (Oct 21). Beside any other
 * status — waiting on you, not started — a live run is news the status chip does not carry. Shared 1:1 with
 * native `TaskGroupStatus.showsWorkingNow`.
 */
export function groupShowsWorkingNow(status: TaskGroupStatus, workingNow: number): boolean {
  return workingNow > 0 && status !== 'active';
}

/** The fold header's badge: the countable label key for `tasks.length` (interpolate `{count}`) + style. */
export function taskGroupStatusBadge(tasks: BucketableTask[], now: number = Date.now()): TaskStatusBadge & { status: TaskGroupStatus } {
  const status = taskGroupStatus(tasks, now) ?? 'active';
  const copy = TASK_GROUP_STATUS_COPY[status];
  return { status, labelKey: tasks.length === 1 ? copy.one : copy.other, variant: copy.variant };
}

// A status DOT, a count pill on a tab, or a stat number is NOT a badge — the DS has no
// component for those — but each must match the badge the same status wears, or the app
// tells two stories about one task. These return that badge's own token, so the two can
// never drift apart. **Never** use them to paint something badge-shaped: that is `<Badge>`,
// and `test/badge-spec.test.ts` fails on a hand-rolled one.
//
// Spelled out rather than built by template, so `test/design-tokens.test.ts` can verify
// every one of these tokens really exists. Each entry is the SAME `--ds-badge-*` pair its
// Badge Style reads in `Badge.module.css` — five of them once pointed at the deleted
// `--badge-*` role layer instead, whose names the token guard's `--ds/--sym` prefix scan
// cannot see, so they resolved to nothing at runtime while every test stayed green
// (`badge-spec.test.ts` now pins the map to defined tokens).
const STATUS_TOKENS: Record<TokenBackedBadgeVariant, { fill: string; ink: string }> = {
  'waiting-on-a-reply': { fill: 'var(--ds-badge-fill-waiting-on-a-reply)', ink: 'var(--ds-badge-text-waiting-on-a-reply)' },
  'waiting-on-you': { fill: 'var(--ds-badge-fill-waiting-on-you)', ink: 'var(--ds-badge-text-waiting-on-you)' },
  'in-progress': { fill: 'var(--ds-badge-fill-in-progress)', ink: 'var(--ds-badge-text-in-progress)' },
  done: { fill: 'var(--ds-badge-fill-done)', ink: 'var(--ds-badge-text-done)' },
  paused: { fill: 'var(--ds-badge-fill-paused)', ink: 'var(--ds-badge-text-paused)' },
  failed: { fill: 'var(--ds-badge-fill-failed)', ink: 'var(--ds-badge-text-failed)' },
  connected: { fill: 'var(--ds-badge-fill-connected)', ink: 'var(--ds-badge-text-connected)' },
  agents: { fill: 'var(--ds-badge-fill-agents-group)', ink: 'var(--ds-text-primary)' },
  'agent-profession': { fill: 'var(--ds-badge-fill-agent-profession)', ink: 'var(--ds-badge-text-agent-profession)' },
};

export const statusFill = (variant: TokenBackedBadgeVariant): string => STATUS_TOKENS[variant].fill;
export const statusInk = (variant: TokenBackedBadgeVariant): string => STATUS_TOKENS[variant].ink;

/**
 * The badge for a whole task ROW — the entry point every render site should use.
 *
 * `taskStatusBadge` takes a STATUS STRING, so four of its five inputs have to be derived by the caller
 * and passed as flags. That is exactly how the "Working" split shipped half-inert the first time: the
 * label existed, the rule read it, and not one render site passed `inMotion` — so the new word could
 * never appear on screen. Four call sites, four chances to forget, and a forgotten flag is invisible to
 * review because each site looks complete on its own.
 *
 * So the derivation lives here, once, over the row that already carries every input. A site that has
 * the task calls this and cannot omit anything; `taskStatusBadge` stays for the two places that genuinely
 * hold only a status (the tab pills, a linked-task chip).
 */
export function taskRowBadge(
  task: BucketableTask & { status?: string; rowKind?: string },
  opts?: { detail?: boolean; now?: number },
): TaskStatusBadge {
  const now = opts?.now ?? Date.now();
  return taskStatusBadge(task.status ?? '', {
    detail: opts?.detail,
    awaitingDecision: awaitsDecision(task),
    notStarted: isNotStarted(task, now),
    outreach: task.rowKind === 'outreach',
    inMotion: isInMotion(task, now),
  });
}
